import React from 'react';
import RDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.css';
import ru from 'date-fns/locale/ru';
import { IconCalendar } from '@app/icons';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from 'lib/utils';

registerLocale('ru', ru);

type Props = {
  value: Date | null | undefined;
  onChange: (v: Date | null, name?: string) => void;
  error?: boolean;
  label?: string;
  helperText?: string | null;
  width?: number;
  name?: string;
  disabled?: boolean;
  placeholderText?: string;
  minDate?: Date;
  maxDate?: Date;
  rightIcon?: React.ReactNode;
  required?: boolean;
  labelClassName?: string;
  disablePastDates?: boolean;
};

export const DatePicker: React.FC<Props> = ({
  value,
  onChange,
  error = false,
  label,
  helperText = '',
  width,
  name,
  disabled,
  placeholderText,
  maxDate,
  minDate,
  rightIcon,
  required = false,
  labelClassName,
  disablePastDates = false,
}) => {
  return (
    <div className="flex flex-col gap-1" style={{ width }}>
      {label && (
        <Label className={cn('text-xs text-muted-foreground', labelClassName)}>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      <div className="relative flex items-center">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10">
          <IconCalendar />
        </span>
        <RDatePicker
          selected={value}
          onChange={(d: any) => onChange(d, name)}
          locale="ru"
          dateFormat="dd.MM.yyyy"
          placeholderText={placeholderText ?? 'ДД.ММ.ГГГГ'}
          name={name}
          disabled={disabled}
          maxDate={maxDate}
          minDate={minDate}
          autoComplete="off"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          yearDropdownItemNumber={100}
          scrollableYearDropdown
          filterDate={
            disablePastDates
              ? (date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const d = new Date(date);
                  d.setHours(0, 0, 0, 0);
                  return d >= today;
                }
              : undefined
          }
          dayClassName={
            disablePastDates
              ? (date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const d = new Date(date);
                  d.setHours(0, 0, 0, 0);
                  return d < today ? 'react-datepicker__day--disabled' : '';
                }
              : undefined
          }
          customInput={
            <Input
              className={cn(
                'pl-10',
                error && 'border-destructive focus-visible:ring-destructive'
              )}
              readOnly
              inputMode="none"
              onFocus={(e) => e.target.blur()}
            />
          }
          popperClassName="react-datepicker-popper"
          calendarClassName="react-datepicker-calendar"
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
            {rightIcon}
          </div>
        )}
      </div>
      {helperText && (
        <div
          className={cn(
            'text-xs',
            error ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

export default React.memo(DatePicker);
